import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Theme,
  Typography,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import {
  CloudDownload,
  CloudUpload,
  Close,
  Description,
  Publish,
} from "@material-ui/icons";
import { AxiosInstance } from "axios";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFormState } from "react-final-form";
import {
  Button as RaButton,
  SelectArrayInput,
  SelectInput,
  SimpleForm,
  TextInput,
  Title,
  useAuthenticated,
  useNotify,
  usePermissions,
} from "react-admin";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { ProLogsPermissions } from "../../../common/auth/permissions";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import { useAppData } from "../../../common/hooks/useAppData";
import {
  useAxiosBaseURL,
  useAxiosInstance,
} from "../../../common/hooks/useAxiosInstance";
import { AppState } from "../../../common/types";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import { RaDriverPicker } from "../../common/form/RaDriverPicker";
import { RaVehiclePicker } from "../../common/form/RaVehiclePicker";
import {
  createDataTransferExport,
  createDataTransferImport,
  DataTransferCategoryKey,
  DataTransferHistoryRequest,
  DataTransferMode,
  getDataTransferHistory,
  getDataTransferJob,
  uploadDataTransferImportFile,
} from "./dataTransferApi";
import {
  DataTransferCategoryConfig,
  DataTransferChoiceSource,
  DataTransferFilterField,
  DataTransferHistoryColumn,
  getDataTransferCategory,
} from "./dataTransferConfig";

type SortOrder = "ASC" | "DESC";

type HistoryState = {
  rows: any[];
  totalCount: number;
  page: number;
  perPage: number;
  search: string;
  status: string;
  sortField: string;
  sortOrder: SortOrder;
  loading: boolean;
  error: string | null;
};

type SummaryDialogState = {
  open: boolean;
  title: string;
  payload: any;
};

type SummaryItem = {
  label: string;
  value: number | string;
};

type TrackedJobStore = Partial<
  Record<
    DataTransferCategoryKey,
    Partial<Record<DataTransferMode, string[]>>
  >
>;

const TRACKED_JOB_STORAGE_KEY = "prologs.data-transfer.tracked-jobs";

const DEFAULT_HISTORY_STATE: HistoryState = {
  rows: [],
  totalCount: 0,
  page: 0,
  perPage: 25,
  search: "",
  status: "",
  sortField: "creationTime",
  sortOrder: "DESC",
  loading: false,
  error: null,
};

const EXPORT_SUCCESS_STATUSES = new Set(["succeeded", "2"]);
const EXPORT_FAILURE_STATUSES = new Set(["failed", "3"]);
const IMPORT_SUCCESS_STATUSES = new Set(["importsuccess", "6"]);
const IMPORT_FAILURE_STATUSES = new Set([
  "importfailed",
  "parsingfailed",
  "5",
  "2",
]);

const useStyles = makeStyles((theme: Theme) => ({
  page: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
  },
  pageHeaderCopy: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  toolbarField: {
    minWidth: 220,
  },
  tableProgress: {
    width: "100%",
  },
  emptyState: {
    padding: theme.spacing(6),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  drawerPaper: {
    width: "min(100vw, 720px)",
  },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
  },
  drawerContent: {
    padding: theme.spacing(3),
  },
  formSectionLabel: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  formToolbar: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    paddingTop: theme.spacing(2),
  },
  statusChip: {
    textTransform: "capitalize",
  },
  uploadField: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
  },
  uploadMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: theme.spacing(2),
  },
  summaryCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
}));

const STATUS_FILTER_CHOICES: Record<DataTransferMode, Array<{ id: string; name: string }>> = {
  export: [
    { id: "Created", name: "Created" },
    { id: "Exporting", name: "Exporting" },
    { id: "Succeeded", name: "Succeeded" },
    { id: "Failed", name: "Failed" },
  ],
  import: [
    { id: "Created", name: "Created" },
    { id: "Parsing", name: "Parsing" },
    { id: "ParsingFailed", name: "Parsing failed" },
    { id: "ReadyToImport", name: "Ready to import" },
    { id: "Importing", name: "Importing" },
    { id: "ImportFailed", name: "Import failed" },
    { id: "ImportSuccess", name: "Import success" },
  ],
};

const normalizeStatus = (value: unknown) =>
  String(value ?? "")
    .replace(/\s+/g, "")
    .toLowerCase();

const formatStatusLabel = (value: unknown) =>
  String(value ?? "Unknown")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (char) => char.toUpperCase());

const isExportSuccess = (value: unknown) =>
  EXPORT_SUCCESS_STATUSES.has(normalizeStatus(value));

const isExportFailure = (value: unknown) =>
  EXPORT_FAILURE_STATUSES.has(normalizeStatus(value));

const isImportSuccess = (value: unknown) =>
  IMPORT_SUCCESS_STATUSES.has(normalizeStatus(value));

const isImportFailure = (value: unknown) =>
  IMPORT_FAILURE_STATUSES.has(normalizeStatus(value));

const getJobId = (payload: any) =>
  payload?.jobId ?? payload?.id ?? payload?.data?.jobId ?? payload?.data?.id;

const getUploadId = (payload: any) =>
  payload?.uploadId ??
  payload?.id ??
  payload?.result?.uploadId ??
  payload?.result?.id ??
  payload;

const getDownloadUrl = (payload: any) =>
  payload?.downloadUrl ??
  payload?.result?.downloadUrl ??
  payload?.fileUrl ??
  payload?.file?.downloadUrl;

const parseDateValue = (value: unknown) => {
  if (!value) {
    return null;
  }

  const parsed = value instanceof Date ? value : new Date(`${value}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateToUtcBoundary = (
  value: unknown,
  boundary: "start" | "end"
) => {
  const parsed = parseDateValue(value);

  if (!parsed) {
    return undefined;
  }

  return new Date(
    Date.UTC(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate(),
      boundary === "start" ? 0 : 23,
      boundary === "start" ? 0 : 59,
      boundary === "start" ? 0 : 59,
      boundary === "start" ? 0 : 999
    )
  ).toISOString();
};

const formatTimestamp = (value: unknown, pattern = "MM/dd/yyyy, hh:mm:ss a") => {
  const parsed = parseDateValue(value);
  return parsed ? format(parsed, pattern) : "N/A";
};

const formatPeriodDate = (value: unknown) => {
  const parsed = parseDateValue(value);
  return parsed ? format(parsed, "MM/dd/yyyy") : null;
};

const hasValue = (value: unknown) =>
  value !== undefined && value !== null && value !== "";

const ensureNumericArray = (value: unknown): number[] =>
  Array.isArray(value)
    ? value
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item))
    : [];

const getChoiceSet = (
  source: DataTransferChoiceSource,
  companies: any[],
  groups: any[]
) => {
  switch (source) {
    case "companies":
      return companies ?? [];
    case "groups":
      return groups ?? [];
    case "recordStatus":
      return [
        { id: "Active", name: "Active" },
        { id: "Inactive", name: "Inactive" },
      ];
    case "eldConnection":
      return [
        { id: "Connected", name: "Connected" },
        { id: "NotConnected", name: "Not connected" },
      ];
    default:
      return [];
  }
};

const buildFilterRule = (field: string, value: unknown) => ({
  field,
  operator: "equals",
  value: `${value}`,
});

const buildFilterInput = ({
  rules,
  searchText,
  searchColumns,
  sorting,
}: {
  rules?: Array<{ field: string; operator: string; value: string }>;
  searchText?: string;
  searchColumns?: string[];
  sorting?: string;
}) => {
  const trimmedSearch = searchText?.trim();
  const filter: Record<string, unknown> = {};

  if (rules && rules.length > 0) {
    filter.filterRule = {
      condition: "and",
      filterRules: rules,
    };
  }

  if (trimmedSearch && searchColumns && searchColumns.length > 0) {
    filter.searchRule = {
      text: trimmedSearch,
      columns: searchColumns,
    };
  }

  if (sorting) {
    filter.sorting = sorting;
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
};

const buildSelectionFilter = (
  config: DataTransferCategoryConfig,
  values: Record<string, any>
) => {
  if (config.key === "drivers") {
    return {
      ...(values.status ? { status: values.status } : {}),
      ...(values.companyId ? { companyId: values.companyId } : {}),
    };
  }

  if (config.key === "vehicles") {
    return {
      ...(values.status ? { status: values.status } : {}),
      ...(values.companyId ? { companyId: values.companyId } : {}),
      ...(values.groupId ? { groupId: values.groupId } : {}),
      ...(values.eldStatus === "Connected"
        ? { isEldConnected: "true" }
        : {}),
      ...(values.eldStatus === "NotConnected"
        ? { isEldConnected: "false" }
        : {}),
    };
  }

  return undefined;
};

const buildExportPayload = (
  config: DataTransferCategoryConfig,
  values: Record<string, any>
) => {
  const description = values.description || config.defaultExportDescription;

  switch (config.key) {
    case "drivers": {
      const driverIds = ensureNumericArray(values.driverIds);

      if (driverIds.length > 0) {
        return {
          description,
          driverIds,
        };
      }

      const filter = buildFilterInput({
        rules: [
          ...(values.status ? [buildFilterRule("status", values.status)] : []),
          ...(values.companyId
            ? [buildFilterRule("companyId", values.companyId)]
            : []),
        ],
        sorting: "driverName asc",
      });

      return {
        description,
        ...(filter ? { filter } : {}),
      };
    }
    case "vehicles": {
      const vehicleIds = ensureNumericArray(values.vehicleIds);

      if (vehicleIds.length > 0) {
        return {
          description,
          vehicleIds,
        };
      }

      const filter = buildFilterInput({
        rules: [
          ...(values.status ? [buildFilterRule("status", values.status)] : []),
          ...(values.companyId
            ? [buildFilterRule("companyId", values.companyId)]
            : []),
          ...(values.groupId ? [buildFilterRule("groupId", values.groupId)] : []),
          ...(values.eldStatus
            ? [buildFilterRule("eldStatus", values.eldStatus)]
            : []),
        ],
        sorting: "name asc",
      });

      return {
        description,
        ...(filter ? { filter } : {}),
      };
    }
    case "trailers": {
      const filter = buildFilterInput({
        rules: [
          ...(values.status ? [buildFilterRule("status", values.status)] : []),
          ...(values.companyId
            ? [buildFilterRule("companyId", values.companyId)]
            : []),
        ],
        searchText: values.searchText,
        searchColumns: ["name", "licensePlate", "vin"],
        sorting: "name asc",
      });

      return {
        description,
        ...(filter ? { filter } : {}),
      };
    }
    case "locations": {
      const payload: Record<string, unknown> = {
        description,
      };

      const dateFrom = formatDateToUtcBoundary(values.dateFrom, "start");
      const dateTo = formatDateToUtcBoundary(values.dateTo, "end");

      if (dateFrom) {
        payload.dateFrom = dateFrom;
      }

      if (dateTo) {
        payload.dateTo = dateTo;
      }

      if (values.driverId) {
        payload.driverId = Number(values.driverId);
      }

      if (values.vehicleId) {
        payload.vehicleId = Number(values.vehicleId);
      }

      return payload;
    }
    default:
      return {
        description,
      };
  }
};

const getImportDisplayStatus = (record: any) =>
  record?.importStatus ?? record?.status;

const getSummaryPayload = (payload: any) =>
  payload?.summary ??
  payload?.resultSummary ??
  payload?.importSummary ??
  payload?.statistics ??
  payload?.result ??
  payload;

const getSummaryItems = (payload: any): SummaryItem[] => {
  const summary = getSummaryPayload(payload) ?? {};

  const candidates: Array<[string, Array<string>]> = [
    ["Processed", ["processed", "processedCount", "totalProcessedCount"]],
    ["Imported", ["imported", "importedCount"]],
    ["Created", ["created", "createdCount"]],
    ["Updated", ["updated", "updatedCount"]],
    ["Skipped", ["skipped", "skippedCount"]],
    ["Failed", ["failed", "failedCount"]],
  ];

  return candidates
    .map(([label, keys]) => ({
      label,
      value: keys
        .map((key) => summary?.[key])
        .find((value) => value !== undefined && value !== null),
    }))
    .filter((item): item is SummaryItem => hasValue(item.value));
};

const getRequesterLabel = (record: any) => {
  const parts = [record?.creatorName, record?.creatorSurname].filter(Boolean);
  const fullName = parts.join(" ").trim();

  if (fullName && record?.creatorEmail) {
    return `${fullName} (${record.creatorEmail})`;
  }

  return fullName || record?.creatorEmail || "N/A";
};

const getStatusColor = (mode: DataTransferMode, status: unknown) => {
  if (mode === "export") {
    if (isExportSuccess(status)) {
      return "primary";
    }

    if (isExportFailure(status)) {
      return "secondary";
    }
  }

  if (mode === "import") {
    if (isImportSuccess(status)) {
      return "primary";
    }

    if (isImportFailure(status)) {
      return "secondary";
    }
  }

  return "default";
};

const getFileName = (record: any) =>
  record?.fileName ??
  record?.sourceFileName ??
  record?.uploadedFileName ??
  record?.name ??
  "N/A";

const getProcessedCount = (record: any) =>
  record?.processed ?? record?.totalProcessedCount;

const getDriverLabel = (record: any) =>
  record?.driverName ?? record?.driverFullName ?? "All drivers";

const getVehicleLabel = (record: any) =>
  record?.vehicleName ?? record?.vehicle ?? "All vehicles";

const getPeriodLabel = (record: any) => {
  const from =
    formatPeriodDate(record?.dateFrom ?? record?.timeFrom ?? record?.periodFrom) ??
    null;
  const to =
    formatPeriodDate(record?.dateTo ?? record?.timeTo ?? record?.periodTo) ?? null;

  if (from && to) {
    return `${from} - ${to}`;
  }

  return from ?? to ?? "N/A";
};

const resolveUrl = (url: string, baseURL?: string) => {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  if (url.startsWith("//")) {
    return `${window.location.protocol}${url}`;
  }

  try {
    return new URL(url, baseURL ?? window.location.origin).toString();
  } catch {
    return url;
  }
};

const resolveDownloadUrl = (payload: any, baseURL?: string) => {
  const rawUrl = getDownloadUrl(payload);
  return rawUrl ? resolveUrl(rawUrl, baseURL) : null;
};

const readTrackedJobStore = (): TrackedJobStore => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.sessionStorage.getItem(TRACKED_JOB_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as TrackedJobStore) : {};
  } catch {
    return {};
  }
};

const writeTrackedJobStore = (store: TrackedJobStore) => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(
    TRACKED_JOB_STORAGE_KEY,
    JSON.stringify(store)
  );
};

const readTrackedJobs = (
  category: DataTransferCategoryKey,
  mode: DataTransferMode
) => readTrackedJobStore()?.[category]?.[mode] ?? [];

const trackJob = (
  category: DataTransferCategoryKey,
  mode: DataTransferMode,
  jobId: string
) => {
  const current = readTrackedJobStore();
  const next = new Set(current?.[category]?.[mode] ?? []);
  next.add(jobId);

  writeTrackedJobStore({
    ...current,
    [category]: {
      ...(current?.[category] ?? {}),
      [mode]: Array.from(next),
    },
  });
};

const untrackJob = (
  category: DataTransferCategoryKey,
  mode: DataTransferMode,
  jobId: string
) => {
  const current = readTrackedJobStore();
  const next = new Set(current?.[category]?.[mode] ?? []);
  next.delete(jobId);

  writeTrackedJobStore({
    ...current,
    [category]: {
      ...(current?.[category] ?? {}),
      [mode]: Array.from(next),
    },
  });
};

const useDataTransferHistory = (
  axios: AxiosInstance | undefined,
  category: DataTransferCategoryKey,
  mode: DataTransferMode,
  searchFields: string[]
) => {
  const [state, setState] = useState<HistoryState>(DEFAULT_HISTORY_STATE);

  const loadHistory = useCallback(async () => {
    if (!axios) {
      return;
    }

    setState((current) => ({
      ...current,
      loading: true,
      error: null,
    }));

    try {
      const request: DataTransferHistoryRequest = {
        page: state.page + 1,
        perPage: state.perPage,
        search: state.search,
        status: state.status,
        sortField: state.sortField,
        sortOrder: state.sortOrder,
        searchFields,
      };

      const result = await getDataTransferHistory(axios, category, mode, request);

      setState((current) => ({
        ...current,
        rows: result.items ?? [],
        totalCount: result.totalCount ?? 0,
        loading: false,
      }));
    } catch {
      setState((current) => ({
        ...current,
        loading: false,
        error: "Failed to load history.",
      }));
    }
  }, [
    axios,
    category,
    mode,
    searchFields,
    state.page,
    state.perPage,
    state.search,
    state.sortField,
    state.sortOrder,
    state.status,
  ]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const updateState = useCallback(
    (updater: (current: HistoryState) => HistoryState) => {
      setState((current) => updater(current));
    },
    []
  );

  return {
    state,
    refresh: loadHistory,
    setSearch: (search: string) =>
      updateState((current) => ({
        ...current,
        search,
        page: 0,
      })),
    setStatus: (status: string) =>
      updateState((current) => ({
        ...current,
        status,
        page: 0,
      })),
    setPage: (page: number) =>
      updateState((current) => ({
        ...current,
        page,
      })),
    setPerPage: (perPage: number) =>
      updateState((current) => ({
        ...current,
        perPage,
        page: 0,
      })),
    setSort: (sortField: string) =>
      updateState((current) => ({
        ...current,
        sortField,
        sortOrder:
          current.sortField === sortField && current.sortOrder === "ASC"
            ? "DESC"
            : "ASC",
        page: 0,
      })),
  };
};

const HistoryTable = ({
  config,
  mode,
  historyState,
  baseURL,
  onSearchChange,
  onStatusChange,
  onPageChange,
  onPerPageChange,
  onSortChange,
  onViewSummary,
  onDownloadExport,
}: {
  config: DataTransferCategoryConfig;
  mode: DataTransferMode;
  historyState: HistoryState;
  baseURL?: string;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onSortChange: (sortField: string) => void;
  onViewSummary: (record: any) => void;
  onDownloadExport: (record: any) => void;
}) => {
  const classes = useStyles();
  const columns = config.historyColumns[mode];

  const renderCell = (column: DataTransferHistoryColumn, record: any) => {
    switch (column.key) {
      case "fileName":
        return getFileName(record);
      case "timeRequested":
        return formatTimestamp(record?.creationTime);
      case "requestedBy":
        return getRequesterLabel(record);
      case "description":
        return record?.description ?? "N/A";
      case "status": {
        const status = mode === "import" ? getImportDisplayStatus(record) : record?.status;
        return (
          <Chip
            size="small"
            className={classes.statusChip}
            color={getStatusColor(mode, status)}
            label={formatStatusLabel(status)}
          />
        );
      }
      case "download": {
        const downloadUrl = resolveDownloadUrl(record, baseURL);

        return downloadUrl ? (
          <Button
            color="primary"
            size="small"
            startIcon={<CloudDownload />}
            onClick={() => onDownloadExport(record)}
          >
            Download
          </Button>
        ) : (
          "N/A"
        );
      }
      case "processed": {
        const processedCount = getProcessedCount(record);
        const importStatus = getImportDisplayStatus(record);

        if (!hasValue(processedCount)) {
          return "N/A";
        }

        return isImportSuccess(importStatus) ? (
          <Button color="primary" size="small" onClick={() => onViewSummary(record)}>
            {processedCount}
          </Button>
        ) : (
          processedCount
        );
      }
      case "period":
        return getPeriodLabel(record);
      case "driver":
        return getDriverLabel(record);
      case "vehicle":
        return getVehicleLabel(record);
      default:
        return "N/A";
    }
  };

  return (
    <Paper>
      {historyState.loading && <LinearProgress className={classes.tableProgress} />}
      <Box p={3}>
        <div className={classes.toolbar}>
          <TextField
            className={classes.toolbarField}
            label="Search history"
            variant="outlined"
            size="small"
            value={historyState.search}
            onChange={(event) => onSearchChange(event.target.value)}
          />
          <FormControl variant="outlined" size="small" className={classes.toolbarField}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={historyState.status}
              onChange={(event) => onStatusChange(event.target.value as string)}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUS_FILTER_CHOICES[mode].map((choice) => (
                <MenuItem key={choice.id} value={choice.id}>
                  {choice.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {historyState.error && (
          <Typography color="error" gutterBottom>
            {historyState.error}
          </Typography>
        )}

        {!historyState.loading && historyState.rows.length === 0 ? (
          <div className={classes.emptyState}>
            <Description fontSize="large" />
            <Typography variant="body1">No history records yet.</Typography>
          </div>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column.key} align={column.align ?? "left"}>
                        {column.sortField ? (
                          <TableSortLabel
                            active={historyState.sortField === column.sortField}
                            direction={
                              historyState.sortField === column.sortField
                                ? historyState.sortOrder.toLowerCase() as "asc" | "desc"
                                : "asc"
                            }
                            onClick={() => onSortChange(column.sortField!)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyState.rows.map((record) => (
                    <TableRow key={record.id ?? `${mode}-${record.creationTime}`}>
                      {columns.map((column) => (
                        <TableCell
                          key={`${record.id ?? record.creationTime}-${column.key}`}
                          align={column.align ?? "left"}
                        >
                          {renderCell(column, record)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              rowsPerPageOptions={[25, 50, 100]}
              count={historyState.totalCount}
              page={historyState.page}
              rowsPerPage={historyState.perPage}
              onPageChange={(_, page) => onPageChange(page)}
              onRowsPerPageChange={(event) =>
                onPerPageChange(Number(event.target.value))
              }
            />
          </>
        )}
      </Box>
    </Paper>
  );
};

const ExportFilterField = ({
  field,
  companies,
  groups,
}: {
  field: DataTransferFilterField;
  companies: any[];
  groups: any[];
}) => {
  if (field.type === "text") {
    return <TextInput source={field.source} label={field.label} fullWidth variant="outlined" />;
  }

  const choices = field.choicesSource
    ? getChoiceSet(field.choicesSource, companies, groups)
    : [];

  if (field.type === "multi-select") {
    return (
      <SelectArrayInput
        source={field.source}
        label={field.label}
        choices={choices}
        variant="outlined"
        fullWidth
      />
    );
  }

  return (
    <SelectInput
      source={field.source}
      label={field.label}
      choices={choices}
      variant="outlined"
      fullWidth
      allowEmpty
    />
  );
};

const ExportDrawerForm = ({
  config,
  companies,
  groups,
  onClose,
  onJobCreated,
}: {
  config: DataTransferCategoryConfig;
  companies: any[];
  groups: any[];
  onClose: () => void;
  onJobCreated: (jobId: string) => void;
}) => {
  const classes = useStyles();
  const axios = useAxiosInstance();
  const notify = useNotify();
  const hub = useSelector((state: AppState) => state.fleetManagerHub.hub);

  const ExportFormBody = () => {
    const [submitting, setSubmitting] = useState(false);
    const form = useForm();
    const { values } = useFormState({
      subscription: {
        values: true,
      },
    });
    const selectionFilter = buildSelectionFilter(
      config,
      values as Record<string, any>
    );

    const handleSubmit = async () => {
      if (!axios || submitting) {
        return;
      }

      const currentValues = form.getState().values as Record<string, any>;
      const dateFrom = parseDateValue(currentValues.dateFrom);
      const dateTo = parseDateValue(currentValues.dateTo);

      if (dateFrom && dateTo && dateFrom.getTime() > dateTo.getTime()) {
        notify("Date from cannot be later than date to.", {
          type: "warning",
        });
        return;
      }

      const payload = buildExportPayload(config, currentValues);

      setSubmitting(true);

      try {
        const result = await createDataTransferExport(axios, config.key, payload);
        const jobId = getJobId(result);

        if (!jobId) {
          throw new Error("Export job id is missing.");
        }

        await hub?.invoke("SubscribeToSingleDataTransferExportUpdates", jobId);

        notify(`${config.label} export started.`, {
          type: "info",
        });

        onJobCreated(jobId);
        onClose();
      } catch {
        notify(`Failed to start ${config.label.toLowerCase()} export.`, {
          type: "error",
        });
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <>
        <TextInput source="description" label="Description" fullWidth variant="outlined" />

        {config.key === "locations" ? (
          <>
            <Typography variant="subtitle2" className={classes.formSectionLabel}>
              Period
            </Typography>
            <GenericDateOrTimePicker
              PickerComponent={DatePicker}
              source="dateFrom"
              label="Date from"
              options={{
                autoOk: true,
                format: "MMMM do yyyy",
              }}
            />
            <GenericDateOrTimePicker
              PickerComponent={DatePicker}
              source="dateTo"
              label="Date to"
              options={{
                autoOk: true,
                format: "MMMM do yyyy",
              }}
            />

            <Typography variant="subtitle2" className={classes.formSectionLabel}>
              Optional filters
            </Typography>
            <RaDriverPicker source="driverId" label="Driver" />
            <RaVehiclePicker source="vehicleId" label="Vehicle" />
          </>
        ) : null}

        {config.exportFilterFields.length > 0 && (
          <>
            <Typography variant="subtitle2" className={classes.formSectionLabel}>
              Export filters
            </Typography>
            {config.exportFilterFields.map((field) => (
              <ExportFilterField
                key={field.source}
                field={field}
                companies={companies}
                groups={groups}
              />
            ))}
          </>
        )}

        {config.SelectionComponent && config.idField ? (
          <>
            <Typography variant="subtitle2" className={classes.formSectionLabel}>
              Select specific {config.entityPluralLabel}
            </Typography>
            <config.SelectionComponent source={config.idField} filter={selectionFilter} />
          </>
        ) : null}

        <Divider />
        <div className={classes.formToolbar}>
          <RaButton label="Cancel" onClick={onClose} />
          <Button
            color="primary"
            variant="contained"
            startIcon={submitting ? <CircularProgress size={18} /> : <CloudDownload />}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Starting export..." : "Start export"}
          </Button>
        </div>
      </>
    );
  };

  return (
    <SimpleForm
      redirect={false}
      toolbar={<span />}
      initialValues={{
        description: config.defaultExportDescription,
      }}
    >
      <ExportFormBody />
    </SimpleForm>
  );
};

const ImportDrawerForm = ({
  config,
  onClose,
  onJobCreated,
}: {
  config: DataTransferCategoryConfig;
  onClose: () => void;
  onJobCreated: (jobId: string) => void;
}) => {
  const classes = useStyles();
  const axios = useAxiosInstance();
  const notify = useNotify();
  const hub = useSelector((state: AppState) => state.fleetManagerHub.hub);
  const [description, setDescription] = useState(config.defaultImportDescription);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!axios || submitting) {
      return;
    }

    if (!selectedFile) {
      notify("Choose an .ndjson.gz file to continue.", {
        type: "warning",
      });
      return;
    }

    if (!selectedFile.name.toLowerCase().endsWith(".ndjson.gz")) {
      notify("The import file must use the .ndjson.gz format.", {
        type: "warning",
      });
      return;
    }

    setSubmitting(true);

    try {
      const uploadResult = await uploadDataTransferImportFile(
        axios,
        config.key,
        selectedFile
      );
      const uploadId = getUploadId(uploadResult);

      if (!uploadId) {
        throw new Error("Upload id is missing.");
      }

      try {
        const importResult = await createDataTransferImport(axios, config.key, {
          description: description || config.defaultImportDescription,
          uploadId,
        });

        const jobId = getJobId(importResult);

        if (!jobId) {
          throw new Error("Import job id is missing.");
        }

        await hub?.invoke("SubscribeToSingleDataTransferImportUpdates", jobId);

        notify(`${config.label} import started.`, {
          type: "info",
        });

        onJobCreated(jobId);
        onClose();
      } catch {
        notify(`Failed to start ${config.label.toLowerCase()} import processing.`, {
          type: "error",
        });
      }
    } catch {
      notify(`Failed to upload the ${config.label.toLowerCase()} import file.`, {
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <TextField
        fullWidth
        label="Description"
        variant="outlined"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />
      <div className={classes.uploadField}>
        <Typography variant="subtitle2">Upload source file</Typography>
        <Typography variant="body2" color="textSecondary">
          Upload the exported `.ndjson.gz` file for this resource.
        </Typography>
        <input
          type="file"
          accept=".ndjson.gz"
          onChange={(event) => setSelectedFile(event.target.files?.[0] ?? null)}
        />
        {selectedFile && (
          <div className={classes.uploadMeta}>
            <Publish color="primary" fontSize="small" />
            <Typography variant="body2">{selectedFile.name}</Typography>
          </div>
        )}
      </div>
      <Divider />
      <div className={classes.formToolbar}>
        <RaButton label="Cancel" onClick={onClose} />
        <Button
          color="primary"
          variant="contained"
          startIcon={submitting ? <CircularProgress size={18} /> : <CloudUpload />}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Starting import..." : "Start import"}
        </Button>
      </div>
    </>
  );
};

const DataTransferCategoryPage = () => {
  useAuthenticated();
  const classes = useStyles();
  const notify = useNotify();
  const history = useHistory();
  const location = useLocation();
  const axios = useAxiosInstance();
  const axiosBaseURL = useAxiosBaseURL();
  const { permissions: permissionsState, loading: permissionsLoading } =
    usePermissions<ProLogsPermissions>();
  const permissions = useActionPermissions();
  const companies = useAppData((x) => x.companies) ?? [];
  const groups = useAppData((x) => x.groups) ?? [];
  const hub = useSelector((state: AppState) => state.fleetManagerHub.hub);
  const { category: rawCategory } = useParams<{ category: string }>();
  const config = getDataTransferCategory(rawCategory);
  const query = new URLSearchParams(location.search);
  const initialTab = query.get("tab") === "import" ? "import" : "export";
  const trackedExportJobIdsRef = useRef<Set<string>>(new Set());
  const trackedImportJobIdsRef = useRef<Set<string>>(new Set());

  const [activeTab, setActiveTab] = useState<DataTransferMode>(initialTab);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [summaryDialog, setSummaryDialog] = useState<SummaryDialogState>({
    open: false,
    title: "",
    payload: null,
  });

  const exportHistory = useDataTransferHistory(
    axios,
    (config?.key ?? "drivers") as DataTransferCategoryKey,
    "export",
    config?.historySearchFields.export ?? []
  );
  const importHistory = useDataTransferHistory(
    axios,
    (config?.key ?? "drivers") as DataTransferCategoryKey,
    "import",
    config?.historySearchFields.import ?? []
  );

  useEffect(() => {
    if (!config) {
      trackedExportJobIdsRef.current = new Set();
      trackedImportJobIdsRef.current = new Set();
      return;
    }

    trackedExportJobIdsRef.current = new Set(readTrackedJobs(config.key, "export"));
    trackedImportJobIdsRef.current = new Set(readTrackedJobs(config.key, "import"));
  }, [config]);

  const canAccessPage = useMemo(() => {
    if (!config) {
      return false;
    }

    const actionPermissions = permissionsState?.actionPermissions ?? permissions;

    switch (config.key) {
      case "drivers":
        return Boolean(
          actionPermissions[permissionNames.admin.driverExport.main] ||
            actionPermissions[permissionNames.admin.driverImport.main] ||
            actionPermissions[permissionNames.admin.drivers.main]
        );
      case "vehicles":
        return Boolean(actionPermissions[permissionNames.admin.vehicles.main]);
      case "trailers":
        return Boolean(actionPermissions[permissionNames.admin.trailers.main]);
      case "locations":
        return Boolean(actionPermissions[permissionNames.locations.history.main]);
      default:
        return false;
    }
  }, [config, permissions, permissionsState]);

  useEffect(() => {
    if (permissionsLoading) {
      return;
    }

    if (!config || !canAccessPage) {
      history.replace("/data-import-export");
    }
  }, [canAccessPage, config, history, permissionsLoading]);

  useEffect(() => {
    const tabValue =
      new URLSearchParams(location.search).get("tab") === "import"
        ? "import"
        : "export";
    setActiveTab(tabValue);
  }, [location.search]);

  const openSummaryDialog = useCallback((title: string, payload: any) => {
    setSummaryDialog({
      open: true,
      title,
      payload,
    });
  }, []);

  const handleDownloadExport = useCallback(
    async (record: any) => {
      const downloadUrl = resolveDownloadUrl(record, axiosBaseURL);

      if (downloadUrl) {
        window.open(downloadUrl, "_self");
        return;
      }

      if (!axios || !config || !record?.id) {
        notify("The export file is not available yet.", {
          type: "warning",
        });
        return;
      }

      try {
        const detail = await getDataTransferJob(axios, config.key, "export", record.id);
        const resolvedDownloadUrl = resolveDownloadUrl(detail, axiosBaseURL);

        if (!resolvedDownloadUrl) {
          notify("The export finished, but the download link is not available yet.", {
            type: "warning",
          });
          return;
        }

        window.open(resolvedDownloadUrl, "_self");
        exportHistory.refresh();
      } catch {
        notify("Failed to load the export file.", {
          type: "error",
        });
      }
    },
    [axios, axiosBaseURL, config, exportHistory, notify]
  );

  const handleViewImportSummary = useCallback(
    async (record: any) => {
      if (!axios || !config || !record?.id) {
        return;
      }

      try {
        const detail = await getDataTransferJob(axios, config.key, "import", record.id);
        openSummaryDialog(`${config.label} import summary`, detail);
      } catch {
        notify("Failed to load import summary.", {
          type: "error",
        });
      }
    },
    [axios, config, notify, openSummaryDialog]
  );

  const subscribeToGlobalUpdates = useCallback(() => {
    if (!hub) {
      return;
    }

    hub.invoke("SubscribeToDataTransferExportUpdates").catch(() => undefined);
    hub.invoke("SubscribeToDataTransferImportUpdates").catch(() => undefined);
  }, [hub]);

  const subscribeToTrackedJobs = useCallback(() => {
    if (!hub) {
      return;
    }

    trackedExportJobIdsRef.current.forEach((jobId) => {
      hub.invoke("SubscribeToSingleDataTransferExportUpdates", jobId).catch(
        () => undefined
      );
    });

    trackedImportJobIdsRef.current.forEach((jobId) => {
      hub.invoke("SubscribeToSingleDataTransferImportUpdates", jobId).catch(
        () => undefined
      );
    });
  }, [hub]);

  useEffect(() => {
    if (!hub) {
      return;
    }

    subscribeToGlobalUpdates();
    subscribeToTrackedJobs();

    const handleReconnected = () => {
      subscribeToGlobalUpdates();
      subscribeToTrackedJobs();
    };

    hub.onreconnected(handleReconnected);

    return () => {
      const connection = hub as any;
      if (typeof connection.offreconnected === "function") {
        connection.offreconnected(handleReconnected);
      }
    };
  }, [hub, subscribeToGlobalUpdates, subscribeToTrackedJobs]);

  useEffect(() => {
    if (!hub || !config || !axios) {
      return;
    }

    const removeTrackedExportJob = (jobId: string) => {
      trackedExportJobIdsRef.current.delete(jobId);
      untrackJob(config.key, "export", jobId);
      hub
        .invoke("UnsubscribeFromSingleDataTransferExportUpdates", jobId)
        .catch(() => undefined);
    };

    const removeTrackedImportJob = (jobId: string) => {
      trackedImportJobIdsRef.current.delete(jobId);
      untrackJob(config.key, "import", jobId);
      hub
        .invoke("UnsubscribeFromSingleDataTransferImportUpdates", jobId)
        .catch(() => undefined);
    };

    const handleExportStatusChanged = async (payload: any) => {
      const payloadJobId = getJobId(payload);

      if (payloadJobId) {
        exportHistory.refresh();
      }

      if (!payloadJobId || !trackedExportJobIdsRef.current.has(payloadJobId)) {
        return;
      }

      const status = payload?.status;

      if (isExportSuccess(status)) {
        notify(`${config.label} export is ready.`, {
          type: "success",
        });

        let downloadUrl = resolveDownloadUrl(payload, axiosBaseURL);

        if (!downloadUrl) {
          try {
            const detail = await getDataTransferJob(
              axios,
              config.key,
              "export",
              payloadJobId
            );
            downloadUrl = resolveDownloadUrl(detail, axiosBaseURL);
          } catch {
            notify("Export finished, but the download link could not be loaded.", {
              type: "warning",
            });
          }
        }

        if (downloadUrl) {
          window.open(downloadUrl, "_self");
        } else {
          notify("Export finished, but the download link is not available yet.", {
            type: "warning",
          });
        }

        removeTrackedExportJob(payloadJobId);
      } else if (isExportFailure(status)) {
        notify(`${config.label} export failed.`, {
          type: "error",
        });
        removeTrackedExportJob(payloadJobId);
      }
    };

    const handleImportStatusChanged = async (payload: any) => {
      const payloadJobId = getJobId(payload);

      if (payloadJobId) {
        importHistory.refresh();
      }

      if (!payloadJobId || !trackedImportJobIdsRef.current.has(payloadJobId)) {
        return;
      }

      const status = payload?.status ?? payload?.importStatus;

      if (isImportSuccess(status)) {
        notify(`${config.label} import completed.`, {
          type: "success",
        });

        try {
          const detail = await getDataTransferJob(
            axios,
            config.key,
            "import",
            payloadJobId
          );
          openSummaryDialog(`${config.label} import summary`, detail);
        } catch {
          notify("Import finished, but the summary could not be loaded.", {
            type: "warning",
          });
        }

        removeTrackedImportJob(payloadJobId);
      } else if (isImportFailure(status)) {
        notify(
          normalizeStatus(status) === "parsingfailed"
            ? `${config.label} import parsing failed.`
            : `${config.label} import failed.`,
          {
            type: "error",
          }
        );
        removeTrackedImportJob(payloadJobId);
      }
    };

    hub.on("OnDataTransferExportStatusChanged", handleExportStatusChanged);
    hub.on("OnDataTransferImportStatusChanged", handleImportStatusChanged);

    return () => {
      hub.off("OnDataTransferExportStatusChanged", handleExportStatusChanged);
      hub.off("OnDataTransferImportStatusChanged", handleImportStatusChanged);
    };
  }, [
    axios,
    axiosBaseURL,
    config,
    exportHistory,
    hub,
    importHistory,
    notify,
    openSummaryDialog,
  ]);

  const summaryItems = useMemo(
    () => getSummaryItems(summaryDialog.payload),
    [summaryDialog.payload]
  );

  if (permissionsLoading) {
    return (
      <div className={`content-container ${classes.page}`}>
        <CircularProgress />
      </div>
    );
  }

  if (!config || !canAccessPage) {
    return null;
  }

  const currentHistory = activeTab === "export" ? exportHistory : importHistory;

  const handleTabChange = (_: unknown, value: DataTransferMode) => {
    const nextQuery = new URLSearchParams(location.search);
    nextQuery.set("tab", value);
    history.replace({
      pathname: location.pathname,
      search: nextQuery.toString(),
    });
  };

  return (
    <div className={`content-container ${classes.page}`}>
      <Title title={`${config.label} Data Import / Export`} />

      <div className={classes.pageHeader}>
        <div className={classes.pageHeaderCopy}>
          <Typography variant="h5">{config.label}</Typography>
          <Typography variant="body2" color="textSecondary">
            {config.description}
          </Typography>
        </div>
        <Button onClick={() => history.push("/data-import-export")}>
          Back to categories
        </Button>
      </div>

      <Paper>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value="export" label="Export" />
          <Tab value="import" label="Import" />
        </Tabs>
      </Paper>

      <div>
        <div className={classes.sectionHeader}>
          <div>
            <Typography variant="h6">
              {activeTab === "export" ? "Export history" : "Import history"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Track job status changes and review completed transfers.
            </Typography>
          </div>
          <Button
            color="primary"
            variant="contained"
            startIcon={activeTab === "export" ? <CloudDownload /> : <CloudUpload />}
            onClick={() => setIsDrawerOpen(true)}
          >
            {activeTab === "export"
              ? `Export ${config.label}`
              : `Import ${config.label}`}
          </Button>
        </div>

        <HistoryTable
          config={config}
          mode={activeTab}
          historyState={currentHistory.state}
          baseURL={axiosBaseURL}
          onSearchChange={currentHistory.setSearch}
          onStatusChange={currentHistory.setStatus}
          onPageChange={currentHistory.setPage}
          onPerPageChange={currentHistory.setPerPage}
          onSortChange={currentHistory.setSort}
          onViewSummary={handleViewImportSummary}
          onDownloadExport={handleDownloadExport}
        />
      </div>

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        classes={{ paper: classes.drawerPaper }}
      >
        <div className={classes.drawerHeader}>
          <div>
            <Typography variant="h6">
              {activeTab === "export"
                ? `Export ${config.label}`
                : `Import ${config.label}`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {activeTab === "export"
                ? `Create a new ${config.label.toLowerCase()} export job.`
                : `Upload a .ndjson.gz file and create a new ${config.label.toLowerCase()} import job.`}
            </Typography>
          </div>
          <IconButton onClick={() => setIsDrawerOpen(false)}>
            <Close />
          </IconButton>
        </div>
        <Divider />
        <div className={classes.drawerContent}>
          {activeTab === "export" ? (
            <ExportDrawerForm
              config={config}
              companies={companies}
              groups={groups}
              onClose={() => setIsDrawerOpen(false)}
              onJobCreated={(jobId) => {
                trackJob(config.key, "export", jobId);
                trackedExportJobIdsRef.current.add(jobId);
                exportHistory.refresh();
              }}
            />
          ) : (
            <ImportDrawerForm
              config={config}
              onClose={() => setIsDrawerOpen(false)}
              onJobCreated={(jobId) => {
                trackJob(config.key, "import", jobId);
                trackedImportJobIdsRef.current.add(jobId);
                importHistory.refresh();
              }}
            />
          )}
        </div>
      </Drawer>

      <Dialog
        open={summaryDialog.open}
        onClose={() =>
          setSummaryDialog({
            open: false,
            title: "",
            payload: null,
          })
        }
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{summaryDialog.title || "Import summary"}</DialogTitle>
        <DialogContent dividers>
          {summaryItems.length > 0 ? (
            <div className={classes.summaryGrid}>
              {summaryItems.map((item) => (
                <div key={item.label} className={classes.summaryCard}>
                  <Typography variant="caption" color="textSecondary">
                    {item.label}
                  </Typography>
                  <Typography variant="h6">{item.value}</Typography>
                </div>
              ))}
            </div>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No import summary details are available for this job.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setSummaryDialog({
                open: false,
                title: "",
                payload: null,
              })
            }
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DataTransferCategoryPage;
